// Products page with BiuroPaczka integration

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ShoppingCart as CartIcon,
  FilterList as FilterIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import biuropaczkaData from '@/data/biuropaczka_products.json';
import BiuroPaczkaCatalog from './components/BiuroPaczkaCatalog';
import StockAdjustmentDialog from './components/StockAdjustmentDialog';

// Sample inventory data based on BiuroPaczka products
const createInventoryFromProducts = () => {
  return biuropaczkaData.products.slice(0, 20).map((product, index) => ({
    id: `inv_${product.id}`,
    biuropaczkaId: product.id,
    name: product.name,
    category: product.category,
    currentStock: Math.floor(Math.random() * 50) + 1,
    minLevel: 5 + (index % 10),
    maxLevel: 30 + (index % 20),
    price: Math.floor(Math.random() * 100) + 10, // Generate random price between 10-110 zł
    imageUrl: product.image_url, // Fix: use image_url from JSON
    supplier: 'biuropaczka' as const,
    lastOrdered: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
    status: ['ok', 'warning', 'critical'][Math.floor(Math.random() * 3)] as 'ok' | 'warning' | 'critical'
  }));
};

const inventoryProducts = createInventoryFromProducts();

export default function ProductsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [products, setProducts] = useState(inventoryProducts);
  const [stockAdjustmentOpen, setStockAdjustmentOpen] = useState(false);
  const [selectedProductForAdjustment, setSelectedProductForAdjustment] = useState<any>(null);
  const [editingStock, setEditingStock] = useState<string | null>(null);
  const [tempStockValue, setTempStockValue] = useState<number>(0);

  // Get unique categories
  const categories = Array.from(new Set(inventoryProducts.map(p => p.category)));

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !categoryFilter || product.category === categoryFilter;
      const matchesStatus = !statusFilter || product.status === statusFilter;
      
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchTerm, categoryFilter, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const critical = products.filter(p => p.status === 'critical').length;
    const warning = products.filter(p => p.status === 'warning').length;
    const total = products.length;
    
    return { critical, warning, total };
  }, [products]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      default: return 'success';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'critical': return 'Krytyczny';
      case 'warning': return 'Ostrzeżenie';
      default: return 'OK';
    }
  };

  const handleOrderProduct = (product: any) => {
    // Placeholder for BiuroPaczka integration
    alert(`Zamów ${product.name} - integracja z BiuroPaczka będzie tutaj`);
  };

  const handleAddProductFromCatalog = (biuroPaczkaProduct: any, stockLevels: { min: number; max: number; initial: number }) => {
    const newProduct = {
      id: `inv_${biuroPaczkaProduct.id}_${Date.now()}`,
      biuropaczkaId: biuroPaczkaProduct.id,
      name: biuroPaczkaProduct.name,
      category: biuroPaczkaProduct.category,
      currentStock: stockLevels.initial,
      minLevel: stockLevels.min,
      maxLevel: stockLevels.max,
      price: Math.floor(Math.random() * 100) + 10, // Generate random price
      imageUrl: biuroPaczkaProduct.image_url,
      supplier: 'biuropaczka' as const,
      lastOrdered: new Date(),
      status: stockLevels.initial > stockLevels.min ? 'ok' : 'critical' as 'ok' | 'warning' | 'critical'
    };

    setProducts(prev => [...prev, newProduct]);
    setCatalogOpen(false);
    
    // Show success message
    alert(`Dodano ${biuroPaczkaProduct.name} do magazynu!`);
  };

  // Get existing product IDs to hide them from catalog
  const existingProductIds = products.map(p => p.biuropaczkaId?.toString()).filter(Boolean);

  const handleStockAdjustment = (product: any) => {
    setSelectedProductForAdjustment(product);
    setStockAdjustmentOpen(true);
  };

  const handleSaveStockAdjustment = (productId: string, newStock: number, adjustment: any) => {
    setProducts(prev => prev.map(product => 
      product.id === productId 
        ? { 
            ...product, 
            currentStock: newStock,
            status: newStock <= product.minLevel ? 'critical' : 
                   newStock <= product.minLevel * 1.5 ? 'warning' : 'ok'
          }
        : product
    ));
    
    // Show success message
    alert(`Zaktualizowano stan produktu: ${selectedProductForAdjustment?.name}. Nowy stan: ${newStock} szt.`);
    
    setStockAdjustmentOpen(false);
    setSelectedProductForAdjustment(null);
  };

  const handleQuickStockEdit = (productId: string, currentStock: number) => {
    setEditingStock(productId);
    setTempStockValue(currentStock);
  };

  const handleStockSave = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    setProducts(prev => prev.map(p => 
      p.id === productId 
        ? { 
            ...p, 
            currentStock: tempStockValue,
            status: tempStockValue <= p.minLevel ? 'critical' : 
                   tempStockValue <= p.minLevel * 1.5 ? 'warning' : 'ok'
          }
        : p
    ));
    
    setEditingStock(null);
    // Show quick feedback
    console.log(`Zaktualizowano stan produktu: ${product.name} na ${tempStockValue} szt.`);
  };

  const handleStockCancel = () => {
    setEditingStock(null);
    setTempStockValue(0);
  };

  const handleQuickStockChange = (productId: string, change: number) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newStock = Math.max(0, product.currentStock + change);
    
    setProducts(prev => prev.map(p => 
      p.id === productId 
        ? { 
            ...p, 
            currentStock: newStock,
            status: newStock <= p.minLevel ? 'critical' : 
                   newStock <= p.minLevel * 1.5 ? 'warning' : 'ok'
          }
        : p
    ));
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Zarządzanie produktami
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => setCatalogOpen(true)}
          >
            Dodaj produkt
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Zarządzaj zapasami biurowymi z integracją BiuroPaczka
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <WarningIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {stats.critical}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Produkty krytyczne
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #ffd43b 0%, #fab005 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <WarningIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {stats.warning}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Wymagają uwagi
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #339af0 0%, #228be6 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <InventoryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Łącznie produktów
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
            color: 'white',
            '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.2s' }
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <MoneyIcon sx={{ fontSize: 32, mb: 1, opacity: 0.9 }} />
              <Typography variant="h4" fontWeight="bold" mb={0.5}>
                {Math.floor(products.reduce((sum, p) => sum + ((p.price || 0) * p.currentStock), 0)).toLocaleString()} zł
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Wartość zapasów
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Szukaj produktów..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Kategoria</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Kategoria"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">Wszystkie</MenuItem>
                  <MenuItem value="critical">Krytyczny</MenuItem>
                  <MenuItem value="warning">Ostrzeżenie</MenuItem>
                  <MenuItem value="ok">OK</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('');
                  setStatusFilter('');
                }}
              >
                Wyczyść
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* BiuroPaczka Integration Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Integracja BiuroPaczka:</strong> Produkty poniżej pochodzą z rzeczywistego katalogu BiuroPaczka.pl. 
          Kliknij "Zamów" aby przejść do procesu zamawiania.
        </Typography>
      </Alert>

      {/* Products Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Produkt</TableCell>
              <TableCell>Kategoria</TableCell>
              <TableCell align="center">Stan</TableCell>
              <TableCell align="center">Min/Max</TableCell>
              <TableCell>Poziom zapasów</TableCell>
              <TableCell align="right">Cena</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow 
                key={product.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                {/* Product with image */}
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      src={product.imageUrl || undefined}
                      alt={product.name}
                      sx={{ 
                        width: 50, 
                        height: 50,
                        bgcolor: 'grey.100'
                      }}
                      variant="rounded"
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {product.name.length > 50 ? `${product.name.substring(0, 50)}...` : product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {product.id.split('_')[1]}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* Category */}
                <TableCell>
                  <Chip 
                    label={product.category} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>

                {/* Current Stock - Editable */}
                <TableCell align="center">
                  {editingStock === product.id ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type="number"
                        value={tempStockValue}
                        onChange={(e) => setTempStockValue(Math.max(0, parseInt(e.target.value) || 0))}
                        inputProps={{ 
                          min: 0,
                          style: { textAlign: 'center', width: '60px' }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleStockSave(product.id);
                          } else if (e.key === 'Escape') {
                            handleStockCancel();
                          }
                        }}
                        autoFocus
                      />
                      <Box display="flex" gap={0.5}>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleStockSave(product.id)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={handleStockCancel}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Box 
                        onClick={() => handleQuickStockEdit(product.id, product.currentStock)}
                        sx={{ 
                          cursor: 'pointer', 
                          p: 1, 
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold">
                          {product.currentStock}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          szt
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="center" gap={0.5} mt={0.5}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickStockChange(product.id, -1);
                          }}
                          disabled={product.currentStock <= 0}
                          sx={{ width: 20, height: 20 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickStockChange(product.id, 1);
                          }}
                          sx={{ width: 20, height: 20 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </TableCell>

                {/* Min/Max */}
                <TableCell align="center">
                  <Typography variant="body2">
                    {product.minLevel} / {product.maxLevel}
                  </Typography>
                </TableCell>

                {/* Stock Level Progress */}
                <TableCell sx={{ minWidth: 120 }}>
                  <Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((product.currentStock / product.maxLevel) * 100, 100)}
                      color={
                        product.status === 'critical' ? 'error' : 
                        product.status === 'warning' ? 'warning' : 'success'
                      }
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" mt={0.5}>
                      {Math.round((product.currentStock / product.maxLevel) * 100)}%
                    </Typography>
                  </Box>
                </TableCell>

                {/* Price */}
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="medium" color="primary">
                    {product.price ? product.price.toLocaleString() : '0'} zł
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell align="center">
                  <Chip
                    label={getStatusLabel(product.status)}
                    color={getStatusColor(product.status) as any}
                    size="small"
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center">
                    <Tooltip title="Zamów produkt">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOrderProduct(product)}
                        disabled={product.status === 'ok'}
                      >
                        <CartIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Korekta stanu">
                      <IconButton 
                        size="small" 
                        color="secondary"
                        onClick={() => handleStockAdjustment(product)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Empty State */}
      {filteredProducts.length === 0 && (
        <Box textAlign="center" py={8}>
          <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Brak produktów spełniających kryteria
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Spróbuj zmienić filtry lub dodać nowe produkty
          </Typography>
        </Box>
      )}

      {/* BiuroPaczka Catalog Dialog */}
      <BiuroPaczkaCatalog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onAddProduct={handleAddProductFromCatalog}
        existingProductIds={existingProductIds}
      />

      {/* Stock Adjustment Dialog */}
      <StockAdjustmentDialog
        open={stockAdjustmentOpen}
        product={selectedProductForAdjustment}
        onClose={() => {
          setStockAdjustmentOpen(false);
          setSelectedProductForAdjustment(null);
        }}
        onSave={handleSaveStockAdjustment}
      />
    </Box>
  );
}